import React, { useCallback, useEffect, useRef } from "react";
import { useEventContext } from "../../context/EventContext";
import { useLocaleContext } from "../../context/LocaleContext";
import { useModalContext } from "../../context/ModalContext";
import Message from "../Messages/Message";
import FixedMessages from "../Messages/FixedMessages";
import Filters from "../Messages/Filters";
import VirtualScroll from "../../modules/VirtualScroll";
import UserAuthenticationModal from "../User/UserAuthenticationModal";
import { TYPE_HYBRID } from "../../constants/common";
import "./style.scss";
import LoadMoreButton from "../Messages/LoadMoreButton";
import { isFirefox } from "../../utils/browserDetection";

export default ({
    flattenedMessages,
    fixedMessages,
    likesEnabled,
    dislikesEnabled,
    currentUser,
    sort, filter,
    sortValues, filterValues,
    updateSort, updateFilter,
    scrollData,
    scrollAtStart,
    updateScrollData,
    avatarsDisabled,
    updateMessage,
    unreadChildrenMessages,
    unreadParentMessages,
    getMessagesMore,
    hasMore,
    isLoadingMore,
    moduleRef,
    onReadAll,
    onWriteMessage,
    stream,
    keyboardShowHandler,
    messageSent,
    isAnswerSupported,
}) => {
    const scrollerRef = useRef(null);
    const { renderModal } = useModalContext();
    const $locale = useLocaleContext();
    const { event } = useEventContext();
    const className = 'question-messages';

    const checkAuth = useCallback((currentUser) => {
        return new Promise((resolve) => {
            if (!currentUser) {
                renderModal(UserAuthenticationModal, {
                    event,
                    $locale,
                    onClose: (props) => resolve(props?.isAuth),
                }, null, true, moduleRef.current)
            } else {
                resolve(true);
            }
        });
    }, [event, $locale]);

    const scrollToStart = (force) => {
        onReadAll();
        if (scrollerRef.current) {
            scrollerRef.current.scrollToStart(force);
        }
    };

    useEffect(() => {
        scrollToStart(true);
    }, [messageSent])

    const scrollToFirstUnreadChild = () => {
        const firstUnreadChildId = unreadChildrenMessages[0]?.id;
        if (firstUnreadChildId && scrollerRef.current) {
            scrollerRef.current.scrollToID(firstUnreadChildId)
        }
    };

    const checkLastChild = (message) => {
        if (!message.parentId) return false;
        const index = flattenedMessages.findIndex(({ id }) => id === message.id);
        return !flattenedMessages[index + 1] || !flattenedMessages[index + 1].parentId
    };

    return (
        <div className={className}>
            <div className={`${className}--filters`}>
                <Filters
                    filterValues={sortValues}
                    currentValue={sort || sortValues.find(({ isDefault }) => isDefault)?.value}
                    changeFilter={updateSort}
                />
                {event.moduleQuestion.allowEpisodeMessage && event.moduleEpisode.enabled && (
                    <Filters
                        filterValues={filterValues}
                        currentValue={filter || filterValues.find(({ isDefault }) => isDefault)?.value}
                        changeFilter={updateFilter}
                    />
                )}
            </div>

            <FixedMessages messages={fixedMessages} type={'question'} />

            <div className="messages-container" id="messages-container">
                {!!flattenedMessages.length ?
                    <VirtualScroll
                        key={stream.id}
                        ref={scrollerRef}
                        className="messages-container-main"
                        data={flattenedMessages}
                        isLoading={isLoadingMore}
                        cache={scrollData}
                        onScrollAtStart={scrollAtStart => updateScrollData({ scrollAtStart })}
                        onUnmount={updateScrollData}
                        renderItem={(message => <Message
                            message={message}
                            onWriteAnswer={isAnswerSupported && onWriteMessage}
                            type={'question'}
                            currentUser={currentUser}
                            fixedLine={!isFirefox && 5}
                            checkAuth={checkAuth}
                            stream={stream}
                            episodes={stream?.episode}
                            moduleRef={moduleRef}
                            isHybrid={event.participationType === TYPE_HYBRID}
                            avatarsDisabled={avatarsDisabled}
                            keyboardShowHandler={keyboardShowHandler}
                            likesEnabled={likesEnabled}
                            dislikesEnabled={dislikesEnabled}
                            isLastChild={checkLastChild(message)}
                        />)}
                        onItemInView={message => message.unread && updateMessage({ ...message, unread: false })}
                        loadMoreButton={hasMore && <LoadMoreButton
                            isLoading={isLoadingMore}
                            onClick={getMessagesMore}
                            isShow={hasMore}
                            title={$locale.get("messages.loadMore")}
                            isBorder
                        />}
                    />
                :
                    <span className="messages-container-label-empty">
                        {$locale.get("messages.listEmpty")}
                    </span>
                }
                <div className="scroll-to-message-container">
                    {!!unreadChildrenMessages.length && (
                        <button
                            className="scroll-to-bottom -thread"
                            onClick={scrollToFirstUnreadChild}
                        >
                            @
                            {!!unreadChildrenMessages.length && <div className="scroll-to-bottom-indicator">{unreadChildrenMessages.length}</div>}
                        </button>
                    )}
                    {!scrollAtStart && (
                        <button
                            className="scroll-to-bottom"
                            onClick={scrollToStart}
                        >
                            <div className="scroll-to-bottom-arrow" />
                            {!!unreadParentMessages.length && <div className="scroll-to-bottom-indicator">{unreadParentMessages.length}</div>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
